'use client'

import { LuneTheme } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import { ReactNode } from 'react'

import '@fontsource/space-grotesk'

import '@fontsource/space-grotesk/700.css'

export interface SelectableChipProps<T> {
    value: T
    label: string
    icon: ReactNode
    disable?: boolean
}

export interface SelectableChipsProps<T> {
    title: ReactNode
    chips: SelectableChipProps<T>[]
    multiSelect?: boolean
    onSelect: (selected: T[]) => void
    selected: T[]
    chipsColors: {
        background: string
        text: string
    }
}

function SelectableChips<T>({
    title,
    chips,
    multiSelect,
    chipsColors,
    onSelect,
    selected,
}: SelectableChipsProps<T>) {
    const { palette } = LuneTheme

    const onClick = (chipValue: T) => {
        let selection = [...selected]
        if (multiSelect) {
            const index = selection.indexOf(chipValue)
            if (index > -1) {
                if (selection.length > 1) {
                    selection.splice(index, 1)
                }
            } else {
                selection.push(chipValue)
            }
        } else {
            if (selected[0] !== chipValue) {
                selection = [chipValue]
            }
        }
        onSelect(selection)
    }

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 3,
                flexDirection: 'column',
                fontFamily: 'Space Grotesk',
            }}
        >
            <Text
                variant={'body3'}
                sx={{
                    fontSize: '18px',
                    lineHeight: '21.6px',
                    fontFamily: 'Space Grotesk',
                    fontWeight: 600,
                    color: palette.Grey900,
                }}
            >
                {title}
            </Text>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                {chips.map(({ label, value, icon, disable }, index) => (
                    <Box
                        key={index}
                        sx={{
                            border: '1px solid',
                            borderColor: chipsColors.text,
                            cursor: disable ? 'default' : 'pointer',
                            pl: 1.5,
                            pr: 3,
                            height: '32px',
                            lineHeight: '32px',
                            borderRadius: 10,
                            width: 'max-content',
                            opacity: disable ? 0.2 : 0.5,
                            fontSize: '12px',
                            color: chipsColors.text,
                            transition: 'opacity .3s ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 2,
                            svg: {
                                height: 16,
                                width: 16,
                            },
                            ...(!disable && {
                                '&:hover': {
                                    transition: 'none',
                                    opacity: 1,
                                },
                            }),
                            ...(selected.includes(value) && {
                                opacity: 1,
                                backgroundColor: chipsColors.background,
                            }),
                        }}
                        onClick={() => {
                            if (!disable) {
                                onClick(value)
                            }
                        }}
                    >
                        {icon}
                        {label}
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export default SelectableChips
